/**
 * Slim entry point for Bid Analysis.
 * Fetches bid details, then delegates to NC, MAS Direct, or MAS Indirect template.
 */
import { useStore } from 'hooks/useStore';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import NCTemplate from './templates/NC/NCTemplate';
import MASDirectTemplate from './templates/MASDirect/MASDirectTemplate';
import MASIndirectTemplate from './templates/MASIndirect/MASIndirectTemplate';
import classes from './bidAnalysisTemplate.module.scss';

const BidAnalysisTemplate = observer(() => {
  const { bidId } = useParams<{ bidId: string }>();
  const { BidAnalysisStore } = useStore();
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  useEffect(() => {
    if (!bidId) {
      setInitialLoadDone(true);
      return;
    }
    setInitialLoadDone(false);
    let cancelled = false;
    const run = async () => {
      await BidAnalysisStore.fetchBidDetails(parseInt(bidId));
      if (!cancelled) setInitialLoadDone(true);
    };
    run();
    return () => {
      cancelled = true;
    };
    // Only re-run when bidId changes; store from context is safe to use without being a dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidId]);

  const bidDetails = BidAnalysisStore.currentBidDetails;
  // Only show loading until our initial fetch completes; ignore store.isLoadingBidDetails so child
  // template fetches (e.g. Full's loadBidDetails) don't flip us back to skeleton and cause a loop
  const loading = !!bidId && !initialLoadDone;

  const requestNavigation = (path: string) => {
    window.location.href = path;
  };

  if (loading) {
    return (
      <div className={classes.templateWrapper}>
        <div className={classes.loadingContainer}>
          <div className={classes.loadingSpinner}>
            <ProgressSpinner style={{ width: '100%', height: '100%' }} />
          </div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!bidDetails && bidId) {
    return (
      <div className={classes.templateWrapper}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <i
              className="pi pi-exclamation-triangle"
              style={{ fontSize: '3rem', color: '#f59e0b' }}
            />
            <h3 style={{ marginTop: '1rem' }}>Bid Not Found</h3>
            <p style={{ color: '#6b7280' }}>Unable to load bid details for ID: {bidId}</p>
            <Button
              label="Go to Dashboard"
              icon="pi pi-arrow-left"
              onClick={() => requestNavigation('/bdportal/bid-analysis/dashboard')}
              style={{ marginTop: '1rem' }}
            />
          </div>
        </div>
      </div>
    );
  }

  const templateType = bidDetails?.template_type || 'mas';
  const productType = bidDetails?.product_type ?? null;

  if (templateType === 'nc') {
    return <NCTemplate />;
  }
  if (productType === 'direct') {
    return <MASDirectTemplate />;
  }
  return <MASIndirectTemplate />;
});

export default BidAnalysisTemplate;
